/**
 * AssignDriverDialog - Dialog for assigning a driver (user with driver role) to a run
 * 
 * Drivers are users with role="driver". They have a color for map markers
 * and can log in to see their assigned runs in Driver View.
 */
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { User, Phone, Check } from 'lucide-react';

const AssignDriverDialog = ({
  open,
  onOpenChange,
  driverUsers = [],
  currentUserId,
  onAssignUser,
  onUnassignUser,
  runName,
  offlineProposal = false,
  status = 'ready',
  error = null,
  closeOnSubmit = true,
  pending = false,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {offlineProposal ? 'Propose Driver Offline' : 'Assign Driver'}
          </DialogTitle>
          <DialogDescription>
            {offlineProposal
              ? `Select a cached driver for "${runName}". This is a local proposal and will not notify the driver.`
              : `Select a driver for "${runName}"`}
          </DialogDescription>
        </DialogHeader>

        {/* Driver Users List */}
        <div className="space-y-2">
          <div className="max-h-[300px] overflow-y-auto space-y-1">
            {status === 'loading' ? (
              <p role="status" className="py-8 text-center text-stone-500">Loading drivers...</p>
            ) : status === 'error' ? (
              <p role="alert" className="py-8 text-center text-red-600">{error || 'Driver list could not be loaded.'}</p>
            ) : driverUsers.length === 0 ? (
              <div className="text-center py-8 text-stone-400 text-sm">
                <User className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="font-medium">No drivers available</p>
                <p className="text-xs mt-1">Add users with "Driver" role in Settings</p>
              </div>
            ) : (
              driverUsers.map((user) => {
                const userColor = user.color || '#6B7280';
                const isSelected = user.id === currentUserId;
                
                return (
                  <button
                    key={user.id}
                    disabled={pending}
                    onClick={() => {
                      if (pending) return;
                      onAssignUser(user.id);
                      if (closeOnSubmit) onOpenChange(false);
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left
                      ${isSelected 
                        ? 'border-primary bg-primary/5 ring-2 ring-blue-200' 
                        : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                      }`}
                    data-testid={`assign-driver-${user.id}`}
                  >
                    {/* Driver color indicator */}
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: userColor }}
                    >
                      <User className="h-4 w-4 text-white" />
                    </div>
                    
                    {/* Driver info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900 truncate">
                        {user.name}
                      </p>
                      {user.phone_number && (
                        <p className="text-xs text-stone-500 flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {user.phone_number}
                        </p>
                      )}
                    </div>
                    
                    {/* Selected indicator */}
                    {isSelected && (
                      <Check className="h-5 w-5 text-primary flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Unassign button if driver is currently assigned */}
        {currentUserId && (
          <div className="pt-3 border-t">
            <Button
              disabled={pending}
              variant="outline"
              className="w-full text-stone-600 hover:text-red-600 hover:border-red-300 hover:bg-red-50"
              onClick={() => {
                if (pending) return;
                onUnassignUser();
                if (closeOnSubmit) onOpenChange(false);
              }}
              data-testid="unassign-driver-btn"
            >
              {offlineProposal ? 'Remove Driver Proposal' : 'Remove Driver Assignment'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssignDriverDialog;
